
import { action, observable, observer, pr, React, theme, ui } from "aaa";
import externalInterfaceStore from "stores/ExternalInterfaceStore";
import trackingStore from "stores/TrackingStore";
import { safeWindowOpen } from "util/helpers";
import { A11yRefocus } from "./A11yRefocus";

export type BackBehaviour = "revoke"
    | "tokenExchange"
    | "none"
    ;

export const defaultBackBehaviour = "tokenExchange";

// Use this to configure the web header from your feature
class GeorgeWebHeaderStore {
    @observable title: React.ReactNode = "";
    @observable backBehaviour: BackBehaviour = defaultBackBehaviour;
    @observable backgroundColor?: string = undefined;

    @action configure(options: {
        title?: React.ReactNode,
        backBehaviour?: BackBehaviour,
        backgroundColor?: string
    }) {
        if (options.title) {
            this.title = options.title;
        } else {
            this.title = document.title;
        }

        if (options.backBehaviour) {
            this.backBehaviour = options.backBehaviour ?? defaultBackBehaviour;
        }

        this.backgroundColor = options.backgroundColor;
    }
}

export const georgeWebHeaderStore = new GeorgeWebHeaderStore();

function getGeorgeUrl() {
    return externalInterfaceStore.federatedLoginUrl;
}

const LogoutButton = observer(() => (
    <div
        style={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            border: "2px solid rgba(255, 255, 255, 0.5)",
            borderRadius: theme.BORDER_RADIUS,
            height: 40,
            paddingLeft: theme.MARGIN - 3, // substract 3px from icon having some empty space
            paddingRight: theme.MARGIN,
        }}
        role="button"
        tabIndex={0}
        onClick={externalInterfaceStore.logoutWebHeader}
        onKeyDown={(e) => {
            if (e.key === "Enter") {
                externalInterfaceStore.logoutWebHeader();
            }
        }}
    >
        <pr.IconPower style={{ color: "white", marginRight: 8 }} />
        {ui.__("common.logout")}
    </div>
));

const BackButton = observer((props: { backBehaviour: BackBehaviour }) => {
    const onBackToGeorge = async () => {
        await trackingStore.trackEvent({ eventName: "viewClosed", });

        if (props.backBehaviour === "revoke") {
            await externalInterfaceStore.revokeToken();
        } else if (props.backBehaviour === "tokenExchange") {
            await externalInterfaceStore.tokenExchange();
        } else if (props.backBehaviour === "none") {
            // none means we have a george token, which cannot be
            // exchanged for another george token -> don't do anything
        }

        safeWindowOpen(getGeorgeUrl());
    };

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                fontSize: 14,
                fontWeight: 600
            }}
            role="button"
            aria-label={ui.__("a11y.backToGeorgeGo")}
            tabIndex={0}
            onClick={onBackToGeorge}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    onBackToGeorge();
                }
            }}
        >
            <pr.IconChevronLeft style={{ color: "white" }} />
            {ui.isMobile ? ui.__("common.back") : ui.__("common.backToGeorgeGo")}
        </div>
    );
});

export const GeorgeWebHeader = observer(({ hideBackButton }: { hideBackButton?: boolean }) => {
    React.useEffect(() => {
        georgeWebHeaderStore.configure({});
    }, []);

    const title = (
        <p
            style={{
                ...pr.styleNoPadding,
                fontSize: 24,
                flexGrow: 1,
                textAlign: "center",
                fontWeight: "bold"
            }}
        >
            {hideBackButton ? (
                <A11yRefocus>
                    {!ui.isMobile && georgeWebHeaderStore.title}
                </A11yRefocus>
            ) : (
                <span>{!ui.isMobile && georgeWebHeaderStore.title}</span>
            )}
        </p>
    );

    return (
        <>
            <div
                className="theme-header-background-color"
                style={{
                    position: "fixed",
                    left: 0,
                    width: "100%",
                    backgroundColor: georgeWebHeaderStore.backgroundColor,
                    height: theme.WEB_HEADER_HEIGHT,
                    color: "white",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxSizing: "border-box",
                    zIndex: 30
                }}
            >
                <div
                    style={{
                        width: "100%",
                        maxWidth: theme.MAX_APP_WIDTH - 36,
                        paddingLeft: 16,
                        paddingRight: 20,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: hideBackButton ? "space-between" : "center",
                    }}
                >
                    {hideBackButton ? <div style={{ width: 60 }} /> : (
                        <A11yRefocus>
                            <BackButton backBehaviour={georgeWebHeaderStore.backBehaviour} />
                        </A11yRefocus>
                    )}
                    {title}
                    <LogoutButton />
                </div>
            </div>
            {/* Placeholder div for content below */}
            <div style={{ height: theme.WEB_HEADER_HEIGHT }} />
        </>
    );
});



// WEBPACK FOOTER //
// ./src/components/common/GeorgeWebHeader.tsx